import type { Edge, Node } from "@xyflow/react"

/**
 * Returns all upstream ancestor nodes of the given node by traversing the
 * reverse graph (following edge.source -> edge.target backwards). The node
 * itself is excluded from the result.
 */
export function getAncestorNodes(nodeId: string, nodes: Node[], edges: Edge[]): Node[] {
  // Build a reverse adjacency map: target -> [sources]
  const reverseAdjacency = new Map<string, string[]>()
  for (const edge of edges) {
    if (!reverseAdjacency.has(edge.target)) {
      reverseAdjacency.set(edge.target, [])
    }
    reverseAdjacency.get(edge.target)?.push(edge.source)
  }

  const ancestorIds = new Set<string>()
  // Index-pointer queue: advancing `head` is O(1), vs. queue.shift() which is
  // O(n) per call (it re-indexes the whole array) — keeps the BFS O(V + E).
  const queue: string[] = [nodeId]
  const visited = new Set<string>([nodeId])

  for (let head = 0; head < queue.length; head++) {
    const current = queue[head]
    const sources = reverseAdjacency.get(current)
    if (!sources) continue
    for (const source of sources) {
      if (visited.has(source)) continue
      visited.add(source)
      ancestorIds.add(source)
      queue.push(source)
    }
  }

  return nodes.filter((node) => ancestorIds.has(node.id))
}

/**
 * Returns a friendly label for a node: the node's `data.label` when it is a
 * non-empty string, otherwise the node type with its first letter capitalized
 * (e.g. `factcheck` -> `Factcheck`).
 */
export function getNodeLabel(node: Node): string {
  const label = node.data.label
  if (typeof label === "string" && label.trim().length > 0) {
    return label
  }

  const type = node.type ?? ""
  if (type.length === 0) return ""

  return type.charAt(0).toUpperCase() + type.slice(1)
}
